import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Container, Row, Col, Card, Button, Badge, Spinner, Alert, Modal } from 'react-bootstrap';
// import { useSession } from '../App'; // Unused - commented out to fix ESLint warning
import MerchantNavigation from './MerchantNavigation';

const Categories = () => {
    console.log('Categories component rendering...');
    const { merchant } = useParams();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    // const { logout } = useSession(); // Unused - commented out to fix ESLint warning

    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showJsonModal, setShowJsonModal] = useState(false);
    const [selectedJson, setSelectedJson] = useState({ title: '', data: null });
    const [lastFetchTime, setLastFetchTime] = useState(null);
    const [isRefreshing, setIsRefreshing] = useState(false);

    // New state for marketplace filtering
    const [selectedMarketplaces, setSelectedMarketplaces] = useState(new Set());
    const [isFiltered, setIsFiltered] = useState(false);

    const API_BASE_URL = 'https://h4gqwxd22m.execute-api.us-east-1.amazonaws.com/beta/integration';
    const CACHE_DURATION = 10 * 60 * 1000; // 10 minutes in milliseconds

    // Marketplace configuration
    const marketplaces = [
        { code: 'mla', name: 'Argentina', flag: '🇦🇷' },
        { code: 'mlc', name: 'Chile', flag: '🇨🇱' },
        { code: 'mlb', name: 'Brasil', flag: '🇧🇷' },
        { code: 'mlm', name: 'Mexico', flag: '🇲🇽' },
        { code: 'mco', name: 'Colombia', flag: '🇨🇴' }
    ];

    // Toggle marketplace selection
    const toggleMarketplace = useCallback((marketplaceCode) => {
        setSelectedMarketplaces(prev => {
            const newSet = new Set(prev);
            if (newSet.has(marketplaceCode)) {
                newSet.delete(marketplaceCode);
            } else {
                newSet.add(marketplaceCode);
            }
            return newSet;
        });
    }, []);

    // Update URL with marketplace filters
    const updateURLWithFilters = (marketplacesSet) => {
        if (marketplacesSet.size > 0) {
            const marketplacesParam = Array.from(marketplacesSet).sort().join(',');
            setSearchParams({ marketplaces: marketplacesParam });
        } else {
            setSearchParams({});
        }
    };

    // Fetch categories from API with optional marketplace filtering
    const fetchCategories = useCallback(async (forceRefresh = false, marketplacesFilter = null) => {
        // Check if data is cached and still valid
        const isCacheValid = () => {
            if (!lastFetchTime) return false;
            return Date.now() - lastFetchTime < CACHE_DURATION;
        };

        // Get cached data from localStorage
        const getCachedData = () => {
            try {
                const cacheKey = marketplacesFilter
                    ? `categories_${merchant}_${Array.from(marketplacesFilter).sort().join('_')}`
                    : `categories_${merchant}`;
                const cached = localStorage.getItem(cacheKey);
                if (cached) {
                    const { data, timestamp } = JSON.parse(cached);
                    if (Date.now() - timestamp < CACHE_DURATION) {
                        return data;
                    }
                }
            } catch (error) {
                console.warn('Error reading from cache:', error);
            }
            return null;
        };

        // Save data to localStorage cache
        const saveToCache = (data) => {
            try {
                const cacheKey = marketplacesFilter
                    ? `categories_${merchant}_${Array.from(marketplacesFilter).sort().join('_')}`
                    : `categories_${merchant}`;
                const cacheData = {
                    data,
                    timestamp: Date.now()
                };
                localStorage.setItem(cacheKey, JSON.stringify(cacheData));
            } catch (error) {
                console.warn('Error saving to cache:', error);
            }
        };

        try {
            if (!forceRefresh && isCacheValid()) {
                return; // Use cached data
            }

            const cachedData = getCachedData();
            if (!forceRefresh && cachedData) {
                setCategories(cachedData);
                // Fix: Define cacheKey here for the timestamp lookup
                const cacheKey = marketplacesFilter
                    ? `categories_${merchant}_${Array.from(marketplacesFilter).sort().join('_')}`
                    : `categories_${merchant}`;
                setLastFetchTime(JSON.parse(localStorage.getItem(cacheKey)).timestamp);
                setLoading(false);
                return;
            }

            setIsRefreshing(true);
            setError(null);

            // Build URL with marketplace filter if specified
            let url = `${API_BASE_URL}/merchant/${merchant}/products?type=categories`;
            if (marketplacesFilter && marketplacesFilter.size > 0) {
                const marketplacesParam = Array.from(marketplacesFilter).join(',');
                url += `&marketplaces=${marketplacesParam}`;
            }

            console.log('Fetching categories with URL:', url);

            const response = await fetch(url);

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();
            const categoriesData = data.data || [];

            // Debug: Log the first category to see the data structure
            if (categoriesData.length > 0) {
                console.log('First category data:', categoriesData[0]);
                console.log('Marketplace fields:', {
                    publish_to_mla: categoriesData[0].publish_to_mla,
                    publish_to_mlc: categoriesData[0].publish_to_mlc,
                    publish_to_mlb: categoriesData[0].publish_to_mlb,
                    publish_to_mlm: categoriesData[0].publish_to_mlm,
                    publish_to_mco: categoriesData[0].publish_to_mco
                });
            }

            setCategories(categoriesData);
            setLastFetchTime(Date.now());
            saveToCache(categoriesData);
        } catch (err) {
            console.error('Error fetching categories:', err);
            setError(err.message);
        } finally {
            setLoading(false);
            setIsRefreshing(false);
        }
    }, [merchant, lastFetchTime, CACHE_DURATION]);

    // Force refresh function
    const handleForceRefresh = () => {
        fetchCategories(true, isFiltered ? selectedMarketplaces : null);
    };

    // Search button handler
    const handleSearch = () => {
        if (selectedMarketplaces.size > 0) {
            setIsFiltered(true);
            updateURLWithFilters(selectedMarketplaces);
            fetchCategories(true, selectedMarketplaces);
        } else {
            // If no marketplaces selected, show all categories
            setIsFiltered(false);
            updateURLWithFilters(new Set());
            fetchCategories(true, null);
        }
    };

    // Initialize marketplace filters from URL and fetch data on component mount
    useEffect(() => {
        const marketplacesParam = searchParams.get('marketplaces');
        if (marketplacesParam) {
            const marketplaceArray = marketplacesParam.split(',').filter(code =>
                marketplaces.some(mp => mp.code === code)
            );
            if (marketplaceArray.length > 0) {
                // Set filter state immediately
                setSelectedMarketplaces(new Set(marketplaceArray));
                setIsFiltered(true);
                // Fetch with marketplace filter
                fetchCategories(false, new Set(marketplaceArray));
                return;
            }
        }

        // No filters in URL, fetch all categories
        fetchCategories();
    }, [merchant]); // eslint-disable-line react-hooks/exhaustive-deps

    // Check cache on browser refresh
    useEffect(() => {
        const handleBeforeUnload = () => {
            // Force refresh on next load
            localStorage.removeItem(`categories_${merchant}`);
        };

        window.addEventListener('beforeunload', handleBeforeUnload);
        return () => window.removeEventListener('beforeunload', handleBeforeUnload);
    }, [merchant]);

    const handleCategoryClick = (categoryId, categoryName) => {
        // Build URL with marketplace filters if they exist
        let url = `/private/${merchant}/products?category_id=${categoryId}&category_name=${encodeURIComponent(categoryName)}`;

        // Add marketplace filters to URL if they exist
        if (selectedMarketplaces.size > 0) {
            const marketplacesParam = Array.from(selectedMarketplaces).sort().join(',');
            url += `&marketplaces=${marketplacesParam}`;
        }

        navigate(url);
    };

    const handleJsonClick = (title, data) => {
        setSelectedJson({ title, data });
        setShowJsonModal(true);
    };

    const merchantInfo = {
        madeluxe: {
            name: 'Madeluxe',
            description: 'Fashion and accessories merchant',
            color: 'primary'
        },
        pawesome: {
            name: 'Pawesome',
            description: 'Pet supplies and accessories merchant',
            color: 'success'
        }
    };

    const currentMerchant = merchantInfo[merchant] || {
        name: merchant,
        description: 'Merchant dashboard',
        color: 'secondary'
    };

    // Format last fetch time
    const formatLastFetchTime = () => {
        if (!lastFetchTime) return 'Never';
        const now = Date.now();
        const diff = now - lastFetchTime;
        const minutes = Math.floor(diff / 60000);
        const seconds = Math.floor((diff % 60000) / 1000);

        if (minutes > 0) {
            return `${minutes}m ${seconds}s ago`;
        }
        return `${seconds}s ago`;
    };

    console.log('Categories component render section, categories:', categories);
    return (
        <>
            <MerchantNavigation currentPage="categories" />

            <Container className="py-5">
                <Row className="mb-4">
                    <Col>
                        <div className="text-center">
                            <h2 className="mb-3">
                                {currentMerchant.name}
                            </h2>
                        </div>
                    </Col>
                </Row>

                {/* Marketplace Filter Section */}
                <Row className="mb-4">
                    <Col>
                        <Card className="shadow-sm">
                            <Card.Body>
                                <div className="d-flex flex-wrap gap-2 mb-3">
                                    {marketplaces.map((marketplace) => (
                                        <Button
                                            key={marketplace.code}
                                            variant={selectedMarketplaces.has(marketplace.code) ? "primary" : "outline-secondary"}
                                            size="sm"
                                            onClick={() => toggleMarketplace(marketplace.code)}
                                            className="d-flex align-items-center gap-2"
                                        >
                                            <span>{marketplace.flag}</span>
                                            {marketplace.name}
                                        </Button>
                                    ))}
                                </div>

                                <div className="d-flex gap-2">
                                    <Button
                                        variant="success"
                                        size="sm"
                                        onClick={handleSearch}
                                        disabled={isRefreshing}
                                    >
                                        {isRefreshing ? (
                                            <>
                                                <Spinner animation="border" size="sm" className="me-2" />
                                                Getting categories...
                                            </>
                                        ) : (
                                            <>
                                                🔍 Filter
                                            </>
                                        )}
                                    </Button>
                                </div>
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>

                <Row>
                    <Col>
                        <Card className="shadow-sm">
                            <Card.Header className="bg-light d-flex justify-content-between align-items-center">
                                <div>
                                    <h5 className="mb-0">
                                        Categories
                                        {isFiltered && (
                                            <Badge bg="info" className="ms-2">
                                                Filtered
                                            </Badge>
                                        )}
                                    </h5>
                                    {lastFetchTime && (
                                        <small className="text-muted">
                                            Last updated: {formatLastFetchTime()}
                                        </small>
                                    )}
                                </div>
                                <div className="d-flex align-items-center gap-2">
                                    {
                                        isRefreshing ? (
                                            <>

                                            </>
                                        ) : (
                                            <Badge bg="info">{categories.length} categories</Badge>
                                        )
                                    }

                                    <Button
                                        variant="outline-primary"
                                        size="sm"
                                        onClick={handleForceRefresh}
                                        disabled={isRefreshing}
                                    >
                                        {isRefreshing ? (
                                            <>
                                                <Spinner animation="border" size="sm" className="me-2" />
                                                Refreshing...
                                            </>
                                        ) : (
                                            <>
                                                🔄 Refresh
                                            </>
                                        )}
                                    </Button>
                                </div>
                            </Card.Header>
                            <Card.Body>
                                {loading && !isRefreshing && (
                                    <div className="text-center py-4">
                                        <Spinner animation="border" variant="primary" />
                                        <p className="mt-2 text-muted">Loading categories...</p>
                                    </div>
                                )}

                                {error && (
                                    <Alert variant="danger">
                                        <strong>Error loading categories:</strong> {error}
                                        <div className="mt-2">
                                            <Button
                                                variant="outline-danger"
                                                size="sm"
                                                onClick={handleForceRefresh}
                                            >
                                                Try Again
                                            </Button>
                                        </div>
                                    </Alert>
                                )}

                                {!loading && !error && categories.length === 0 && (
                                    <Alert variant="info">
                                        <strong>No categories found</strong>
                                        <br />
                                        <small>
                                            {isFiltered
                                                ? "No categories match the selected marketplace criteria."
                                                : "This merchant doesn't have any categories yet."
                                            }
                                        </small>
                                    </Alert>
                                )}

                                {!loading && !isRefreshing && !error && categories.length > 0 && (
                                    <Row>
                                        {categories.map((category, index) => (
                                            <Col key={index} xs={12} className="mb-3">
                                                <Card className="category-card">
                                                    <Card.Body>
                                                        <div className="d-flex justify-content-between align-items-start mb-3">
                                                            <div>
                                                                <h5 className="text-primary mb-1">
                                                                    {category.meli_domain_name}
                                                                    &nbsp;
                                                                    &gt;
                                                                    &nbsp;
                                                                    {category.meli_name}
                                                                </h5>
                                                                <div className="d-flex gap-2">
                                                                    {category.meli_category_id}
                                                                    &nbsp;&nbsp;&nbsp;


                                                                </div>
                                                            </div>
                                                            <div className="d-flex gap-2">
                                                                {category.category_attributes && (
                                                                    <Button
                                                                        variant="outline-info"
                                                                        size="sm"
                                                                        onClick={() => handleJsonClick(
                                                                            `Category Attributes - ${category.meli_name}`,
                                                                            category.category_attributes
                                                                        )}
                                                                    >
                                                                        View JSON
                                                                    </Button>
                                                                )}
                                                                <Button
                                                                    variant="primary"
                                                                    size="sm"
                                                                    onClick={() => handleCategoryClick(category.meli_category_id, category.meli_name)}
                                                                >
                                                                    Products&nbsp;{category.product_count}
                                                                </Button>

                                                            </div>
                                                        </div>

                                                        {/* Marketplace Publishing Section */}
                                                        <div className="mt-3">
                                                            <div className="d-flex flex-wrap gap-2">
                                                                {!!category.publish_to_mla && (
                                                                    <Badge bg="dark" className="fs-6 px-3 py-2">🇦🇷 Argentina</Badge>
                                                                )}
                                                                {!!category.publish_to_mlc && (
                                                                    <Badge bg="dark" className="fs-6 px-3 py-2">🇨🇱 Chile</Badge>
                                                                )}
                                                                {!!category.publish_to_mlb && (
                                                                    <Badge bg="dark" className="fs-6 px-3 py-2">🇧🇷 Brasil</Badge>
                                                                )}
                                                                {!!category.publish_to_mlm && (
                                                                    <Badge bg="dark" className="fs-6 px-3 py-2">🇲🇽 Mexico</Badge>
                                                                )}
                                                                {!!category.publish_to_mco && (
                                                                    <Badge bg="dark" className="fs-6 px-3 py-2">🇨🇴 Colombia</Badge>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </Card.Body>
                                                </Card>
                                            </Col>
                                        ))}
                                    </Row>
                                )}
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>
            </Container>

            {/* JSON Modal */}
            <Modal
                show={showJsonModal}
                onHide={() => setShowJsonModal(false)}
                size="lg"
                centered
            >
                <Modal.Header closeButton>
                    <Modal.Title>{selectedJson.title}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <pre className="json-display">
                        {JSON.stringify(selectedJson.data, null, 2)}
                    </pre>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowJsonModal(false)}>
                        Close
                    </Button>
                </Modal.Footer>
            </Modal>
        </>
    );
};

export default Categories;